import * as React from 'react';

import { PosStatelessScreenClassComponent } from './PosScreenClassComponent';
import { Modal } from 'core/ui-next/modal';
import { ScreenSize } from './ScreenSize';
import { ActionButtonDefinition } from './ActionButton';
import { ScreenSection, ScreenSectionSymbols } from './PosScreen.Sections';

export type ScreenBaseProps = React.PropsWithChildren<{
	size: ScreenSize;
	// Id de pantalla para telemetría y logging
	screenId: string;

	useKeyPad?: boolean;
	useKeyBoard?: boolean;
	opacity?: number;
	isLargeDevice?: boolean;
	header?: React.ReactNode;
	footer?: React.ReactNode;
	sidebar?: React.ReactNode;
}>

// El PosScreenWrapper nunca va a recibir unos Modal.Actions porque sólo lo vamos a usar para pintar cosas. El cancel y el
// close lo van a tener los componentes funcionales que lo usen. Aquí, para que me funcione el tipado engaño a PosStatelessScreenClassComponent
// diciéndole que sus props también tienen un Modal.Actions aunque sea mentira (nos vamos fijar como premisa de que nunca los va a usar cuando
// lo usamos desde un FC). El día de mañana cuando esté todo migrado desparecerá PosStatelessScreenClassComponent y lo convertiremos en un FC.
class PosScreenWrapper extends PosStatelessScreenClassComponent<ScreenBaseProps & Modal.Actions<any>> {

	protected getScreenId(): string {
		return this.props.screenId;
	}

	// No obligo a pasar el title, ni el image, ni los botones. Eso es algo que quiero me pasen ya construido
	// en el header, footer o sidebar. O si acaso que lo monte otro componente por encima como el PosScreen
	protected getTitle(): string {
		return '';
	}

	protected getImage(): string | SvgIcon {
		return '';
	}

	protected getActionButtonsDefs(): Nullable<ActionButtonDefinition>[] {
		return [];
	}

	onComponentDidMount(): void { }
	onComponentWillUnmount(): void { }

	protected getSize(): ScreenSize {
		return this.props.size;
	}

	protected override useKeypad(): boolean {
		return this.props.useKeyPad ?? false;
	}

	protected override useKeyboard(): boolean {
		return this.props.useKeyBoard ?? false;
	}

	protected override getOpacity(): number {
		return this.props.opacity ?? 1;
	}

	protected override isLargeDevice(): boolean {
		return this.props.isLargeDevice ?? true;
	}

	protected override isLegacy(): boolean {
		return false;
	}

	protected override registerDismissHandlers(): boolean {
		return false;
	}

	protected override getContent(): React.ReactNode {
		return this.props.children;
	}

	protected override getFooter(): React.ReactNode {
		return this.props.footer;
	}

	protected override getHeader(): React.ReactNode {
		return this.props.header;
	}

	protected override getSidebar(): React.ReactNode {
		return this.props.sidebar;
	}
}

type PosScreenProps = Omit<ScreenBaseProps, 'header' | 'footer' | 'sidebar'> & {

	// Hack para poder optimizar el rendimiento de algunas transiciones. Hace que la pantalla no se renderice
	// cuando está completamente cubierta por otro diálogo full-screen. No todas las pantallas soportan esto porque implica que se están
	// desmontando sus hijos y, potencialmente, perdiendo su estado, pero hay casos en los que necesitamos hilar
	// fino para sacar más rendimiento, como pasa con el T11 y el S1F2 en las transiciones entre login -> mesas -> venta.
	//
	// OJO: Para usar esta propiedad, el componente que contiene el <PosScreen ..> tiene que tener en cuenta
	// que los hijos se van a remontar al navegar de vuelta a esta pantalla. Si los hijos están manteniendo
	// estado, por ejemplo, un producto seleccionado, una línea de ticket, etc., hay que asegurarse de subir
	// el estado al padre o moverlo a un Store. En general, no es buena idea poner esto a true a menos que
	// sea necesario.
	//
	// No llevo el prop al class component porque depende de la cobertura calculada por ModalProvider y, además,
	// no está pensado para pantallas legacy, sólo para las nuevas.
	showOnlyWhileOnTop?: boolean;
}

const extractScreenSection = (el: React.ReactElement<any>): ScreenSection | undefined => {
	const type = el.type as any;

	if (type?.[ScreenSectionSymbols.Header]) return 'Header';
	if (type?.[ScreenSectionSymbols.Footer]) return 'Footer';
	if (type?.[ScreenSectionSymbols.Sidebar]) return 'Sidebar';
	if (type?.[ScreenSectionSymbols.Content]) return 'Content';

	return undefined;
};

export const PosScreen = React.forwardRef((props: PosScreenProps, ref: React.Ref<HTMLDivElement>) => {

	const { children, showOnlyWhileOnTop } = props;

	const wrapperRef = React.useRef<PosScreenWrapper>(null);

	// Necesitamos el div del screen, para enganchar los lectores de códigos de barras.
	React.useImperativeHandle(ref, () => wrapperRef.current?.getScreenRef().current!);

	const getSections = (element: React.ReactNode): Record<ScreenSection, React.ReactNode | undefined> => {

		const result: Record<ScreenSection | 'Content', React.ReactNode | undefined> = {
			Header: undefined,
			Footer: undefined,
			Sidebar: undefined,
			Content: undefined
		};

		for (const child of React.Children.toArray(element)) {

			if (!React.isValidElement(child)) {
				throw new Error('Cualquier hijo de PosScreen debería ser Header, Footer, Sidebar o Content');
			}

			const section = extractScreenSection(child);
			if (section === undefined)
				throw new Error('Cualquier hijo de PosScreen debería ser Header, Footer, Sidebar o Content');

			result[section] = child;
		}

		return result;
	};

	const sections = getSections(children);

	const screenBaseProps: ScreenBaseProps = {
		...props,
		header: sections['Header'],
		footer: sections['Footer'],
		sidebar: sections['Sidebar'],
		children: sections['Content']
	};

	const isCoveredByOtherScreen = Modal.useIsCoveredByOtherScreen();

	if (showOnlyWhileOnTop && isCoveredByOtherScreen)
		return;

	return <PosScreenWrapper ref={wrapperRef} {...screenBaseProps as any} />;
});
